import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../../UiKit';
import { useQueryCall, useMutationCall } from '../../../../api/react-client';
import { useClient } from '../../../../api/api-client';
import ConfigUpdateForm from './ConfigUpdateForm';
import {
  categoryTreeUrl,
  fetchTasksEndpoint,
  fetchCategoryListEndPoint,
  fetchChangeLogEndpoint,
  ENTITY_TYPE,
  getEntityDetailsEndpoint,
  getEntityPreviewEndpoint,
} from './constants';
import { Lightbulb, Settings, SquareArrowOutUpRight } from 'lucide-react';
import { ReturnConfigModalTitle } from './Elements/ModalTitle';
import toast from 'react-hot-toast';
import SearchBar from './Elements/SearchBar';
import ConfigTasks from './Elements/ConfigTasks';
import Tabs from './Elements/Tabs';

const ReturnConfigDashboard = () => {
  const client = useClient();

  // Entities -> Category, Product
  const [entityId, setEntityId] = useState(null);
  const [selectedEntityType, setSelectedEntityType] = useState(
    ENTITY_TYPE.CATEGORY,
  );
  const [entityName, setEntityName] = useState();

  const [productInput, setProductInput] = useState('');
  const [parentCategoryDetails, setParentCategoryDetails] = useState();
  const [categoryHierarchy, setCategoryHierarchy] = useState();

  const [taskId, setTaskId] = useState();
  const [isTaskClosed, setIsTaskClosed] = useState(false);

  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState([]);
  const [updatedFormData, setUpdatedFormData] = useState([]);

  const [isPreviewMode, setPreviewMode] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState({});
  const [isError, setIsError] = useState(false);

  // fetch category tasks
  const {
    data: tasksDetails,
    isLoading: isTasksLoading,
    refetch: refetchCategoryTasks,
  } = useQueryCall(
    {
      cacheTime: 0,
    },
    fetchTasksEndpoint,
    ['category', 'tasks'],
    () => client(`${fetchTasksEndpoint}`),
  );

  // fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQueryCall(
    {},
    fetchCategoryListEndPoint,
    ['category', 'list'],
    () => client(`${fetchCategoryListEndPoint}`),
  );

  // fetch selected entity details
  const {
    isLoading: isEntityDetailsLoading,
    isError: errorFetchingEntityDetails,
  } = useQueryCall(
    {
      enabled: Boolean(entityId),
      cacheTime: 0,
    },
    getEntityDetailsEndpoint(selectedEntityType, entityId),
    [entityId, 'category-id', 'product-id'],
    () =>
      client(`${getEntityDetailsEndpoint(selectedEntityType, entityId)}`).then(
        (response) => {
          if (!response) return;
          const {
            product_name,
            category_name,
            attributes,
            category_chain,
            category_details,
          } = response || {};

          if (selectedEntityType === ENTITY_TYPE.CATEGORY) {
            const categoryChain = category_chain ?? [];
            setCategoryHierarchy(categoryChain.join(' / '));
            setEntityName(category_name);
          } else if (selectedEntityType === ENTITY_TYPE.PRODUCT) {
            setParentCategoryDetails(category_details);
            setEntityName(product_name);
          }

          setPreviewLoading(false);
          setFormData(attributes || []);
        },
      ),
  );

  // fetch change log details
  const { isLoading: changeLogsLoading, refetch: getChangeLogs } = useQueryCall(
    {
      enabled: false,
      cacheTime: 0,
    },
    fetchChangeLogEndpoint(taskId),
    [taskId],
    () =>
      client(`${fetchChangeLogEndpoint(taskId)}`).then((res) => {
        const result = { changelog: res };
        setPreviewLoading(false);
        setPreviewData(result);
        setPreviewMode(true);
        if (res.success) setIsError(false);
        return result;
      }),
  );

  // update return config data
  const {
    mutate: updateEntityAttributes,
    isLoading: isUpdatingEntityAttributes,
    isError: errorUpdatingEntityAttributes,
  } = useMutationCall(
    {
      cacheTime: 0,
    },
    getEntityPreviewEndpoint(selectedEntityType),
    [],
    {
      doNotShowSuccessToast: true,
    },
    (data) =>
      client(`${getEntityPreviewEndpoint(selectedEntityType)}`, { data }).then(
        (response) => {
          setPreviewLoading(false);
          if (response.success) {
            setIsError(false);
            if (!data['simulate']) {
              toast.success('Configuration updated successfully');
            }
            if (selectedEntityType === ENTITY_TYPE.CATEGORY) {
              refetchCategoryTasks();
            }
          }
          if (data['simulate']) {
            setPreviewData(response);
            setPreviewMode(true);
          } else {
            if (response.success) {
              resetForm();
            }
          }
        },
      ),
  );

  const reviewTask = useCallback((categoryId, taskId, taskClosed = false) => {
    setEntityId(categoryId);
    setSelectedEntityType(ENTITY_TYPE.CATEGORY);
    setTaskId(taskId);
    setIsTaskClosed(taskClosed);
    setOpenForm(true);
  }, []);

  const setPreviewPayload = useCallback(
    ({ payload, id, taskId, isPreviewData }) => {
      const dataToSubmit = {},
        attributes = [];

      for (const { slug, value } of payload) {
        attributes.push({
          slug,
          value,
        });
      }

      dataToSubmit['attributes'] = attributes;
      dataToSubmit['simulate'] = isPreviewData;
      if (id) {
        if (selectedEntityType === ENTITY_TYPE.CATEGORY) {
          dataToSubmit['category_id'] = id;
        } else {
          dataToSubmit['product_id'] = id;
        }
      }
      if (taskId) dataToSubmit['task_id'] = taskId;

      updateEntityAttributes(dataToSubmit);
      setUpdatedFormData(payload);
      setPreviewLoading(isPreviewData);
    },
    [selectedEntityType, updateEntityAttributes],
  );

  const resetForm = useCallback(() => {
    setOpenForm(false);
    setPreviewMode(false);
    setEntityId(null);
    setTaskId(null);
    setFormData(null);
    setPreviewData(null);
    setIsTaskClosed(false);
    setPreviewLoading(false);
    setIsError(false);
  }, []);

  const handleTabClick = useCallback(
    (key) => {
      setSelectedEntityType(key);
      resetForm();
      setEntityName('');
      setCategoryHierarchy('');
      setProductInput('');
    },
    [resetForm],
  );

  useEffect(() => {
    if (errorFetchingEntityDetails || errorUpdatingEntityAttributes) {
      setPreviewLoading(false);
      setIsError(true);
    }
  }, [errorUpdatingEntityAttributes, errorFetchingEntityDetails]);

  useEffect(() => {
    if (entityId) {
      setOpenForm(true);
    }
  }, [entityId]);

  useEffect(() => {
    setUpdatedFormData(formData);
  }, [formData]);

  const options = useMemo(() => {
    if (selectedEntityType === ENTITY_TYPE.CATEGORY && categories?.length) {
      return categories?.map((category) => ({
        label: category.name,
        value: category.id,
      }));
    }
    return [];
  }, [selectedEntityType, categories]);

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10 tw-p-[2rem] tw-rounded-[1rem] tw-relative tw-bg-white'>
      {/* Header */}
      <div className='tw-text-[1.5rem] tw-w-fit tw-bg-orange-200/50 tw-border-solid tw-border-orange-200 tw-border-[1px] tw-rounded-lg tw-pr-4 tw-pl-3 tw-p-2 tw-font-regular tw-flex tw-items-center tw-leading-none tw-text-orange-400'>
        <Lightbulb
          className='tw-mr-2 tw-mt-[1px] hover:tw-animate-spin'
          size={14}
          style={{ animationDuration: '2s' }}
          strokeWidth={2.3}
        />
        Recommended to make changes between 10PM-6AM
      </div>
      <div className='tw-w-full tw-justify-between tw-items-center'>
        <div className='tw-text-[2.2rem] tw-font-medium tw-text-zinc-800 tw-leading- tw-flex tw-items-center tw-leading-none'>
          Return Config Dashboard
          <Settings
            className='tw-ml-3 tw-mt-[1px] hover:tw-animate-spin'
            size={23}
            style={{ animationDuration: '2s' }}
            strokeWidth={2.3}
          />
        </div>
        <a
          href={categoryTreeUrl}
          className='tw-text-[1.4rem] tw-font-medium tw-text-base-green hover:tw-opacity-90 tw-transition-all tw-ease-in-out tw-duration-200 tw-leading-none tw-flex tw-items-center tw-gap-2 tw-cursor-pointer'
          target='_blank'
          rel='noreferrer'
        >
          View Category Tree
          <SquareArrowOutUpRight size={14} strokeWidth={2.3} />
        </a>
      </div>

      {/* Tabs and Search Bar */}
      <div className='tw-flex tw-items-center tw-justify-between tw-border-solid !tw-border-x-0 !tw-border-t-0 tw-border-base-green tw-border-2 tw-p-0 tw-m-0'>
        <Tabs
          selectedEntityType={selectedEntityType}
          handleTabClick={handleTabClick}
        />

        <div className='tw-flex tw-items-center tw-justify-end tw-border-solid !tw-border-x-0 !tw-border-t-0 tw-border-base-green tw-border-2 tw-p-0 tw-m-0 !tw-pb-2'>
          <SearchBar
            openForm={openForm}
            categoriesLoading={categoriesLoading}
            options={options}
            selectedEntityType={selectedEntityType}
            setProductInput={setProductInput}
            setEntityId={setEntityId}
            entityId={entityId}
            productInput={productInput}
          />
        </div>
      </div>

      {/* Tasks */}
      <ConfigTasks
        isTasksLoading={isTasksLoading}
        tasksDetails={tasksDetails}
        selectedEntityType={selectedEntityType}
        reviewTask={reviewTask}
        taskId={taskId}
      />

      {/* Modal */}
      <Modal
        type='large'
        padding={0}
        paddingTop={'0rem'}
        isMaxContent
        maxHeight='90vh'
        containerStyles={{
          margin: '0px 0 0px 0',
          padding: '0 0px',
        }}
        isVisible={openForm}
        modalBgColor='#e8eaf5'
        handleClose={resetForm}
        wrapperId={'return-config-modal'}
        titleComponent={
          !isEntityDetailsLoading && (
            <ReturnConfigModalTitle
              entityName={entityName}
              catHierarchy={categoryHierarchy}
            />
          )
        }
        component={
          <ConfigUpdateForm
            // form related props
            resetForm={resetForm}
            formData={formData}
            setFormData={setFormData}
            updatedFormData={updatedFormData}
            setUpdatedFormData={setUpdatedFormData}
            isFormLoading={isEntityDetailsLoading}
            parentCategoryDetails={parentCategoryDetails}
            //
            entityId={entityId}
            taskId={taskId}
            isTaskClosed={isTaskClosed}
            // preview relates props
            previewData={previewData}
            previewLoading={previewLoading || changeLogsLoading}
            isPreviewMode={isPreviewMode}
            setPreviewMode={setPreviewMode}
            setPreviewPayload={setPreviewPayload}
            //
            getChangeLogs={getChangeLogs}
            isError={isError}
            isSubmitting={isUpdatingEntityAttributes && !previewLoading}
            selectedEntityType={selectedEntityType}
          />
        }
      />
    </div>
  );
};

export default ReturnConfigDashboard;
